<template>
    <div class="layout-sidebar" :class="{ 'layout-sidebar-expanded': isExpanded }">
        <div class="layout-menu-container hide-scrollbar">
            <AppMenu v-if="($isMobile || $isTablet)" />
            <component
                :is="resolvedComponent"
                v-else
                ref="sidebarComponentRef"
                @update:expanded="handleExpansionUpdate"
            />
        </div>
    </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import InstitutionSettingsSidebar from '@/components/administration/institution-settings/InstitutionSettingsSidebar.vue';
import appSidebar from '@/components/app/AppSidebar.vue';
import GroupSidebar from '@/components/groups/GroupSidebar.vue';
import HelpSidebar from '@/components/help/HelpSidebar.vue';
import InboxSidebar from '@/components/inbox/InboxSidebar.vue';
import EditPagesSidebar from '@/components/pages/EditPagesSidebar.vue';
import PagesSidebar from '@/components/pages/pages/PagesSidebar.vue';
import ReportsSidebar from '@/components/reports/ReportsSidebar.vue';
import SidebarMenu from '@/components/sidebar/SidebarMenu.vue';
import UserSidebar from '@/components/users/UserSidebar.vue';
import AppMenu from './AppMenu.vue';
import { useLayout } from './composables/layout.js';

const { layoutState } = useLayout();

const props = defineProps({
    sidebarComponent: {
        type: String,
        default: undefined,
    },
});

const isExpanded = ref(false);
const sidebarComponentRef = ref(null);

// Map kebab-case string names to actual component objects
const components = {
    'app-sidebar': appSidebar,
    'institution-settings-sidebar': InstitutionSettingsSidebar,
    SidebarMenu,
    'user-sidebar': UserSidebar,
    'help-sidebar': HelpSidebar,
    'reports-sidebar': ReportsSidebar,
    'edit-pages-sidebar': EditPagesSidebar,
    'group-sidebar': GroupSidebar,
    'pages-sidebar': PagesSidebar,
    'inbox-sidebar': InboxSidebar,
};

// Resolve the component based on the prop
const resolvedComponent = computed(() => {
    return props.sidebarComponent ? components[props.sidebarComponent] : null;
});

// Handle expansion state from app-sidebar
const handleExpansionUpdate = expanded => {
    isExpanded.value = expanded;
};

// Watch for sidebar being hidden and reset expansion state
watch(
    () => layoutState.staticMenuDesktopInactive,
    isInactive => {
        if (isInactive && isExpanded.value) {
            // Sidebar is being hidden, reset expansion
            isExpanded.value = false;

            // Reset the child component's state if it has the resetToSummary method
            if (sidebarComponentRef.value) {
                sidebarComponentRef.value.resetToSummary?.();
            }
        }
    }
);

// Also watch for overlay menu being closed
watch(
    () => layoutState.overlayMenuActive,
    isActive => {
        if (!isActive && isExpanded.value) {
            // Overlay menu is being closed, reset expansion
            isExpanded.value = false;

            // Reset the child component's state if it has the resetToSummary method
            if (sidebarComponentRef.value) {
                sidebarComponentRef.value.resetToSummary?.();
            }
        }
    }
);

// Also watch for mobile menu being closed
watch(
    () => layoutState.staticMenuMobileActive,
    isActive => {
        if (!isActive && isExpanded.value) {
            // Mobile menu is being closed, reset expansion
            isExpanded.value = false;

            // Reset the child component's state if it has the resetToSummary method
            if (sidebarComponentRef.value) {
                sidebarComponentRef.value.resetToSummary?.();
            }
        }
    }
);
</script>

<style lang="scss" scoped>
.layout-sidebar {
    padding: 1rem;
}

/* Apply this to the element you want to hide the scrollbar for */
.hide-scrollbar {
    -ms-overflow-style: none;  /* IE and Edge */
    scrollbar-width: none;     /* Firefox */
}

/* For Chrome, Safari, and Opera */
.hide-scrollbar::-webkit-scrollbar {
    display: none;             /* Hide the scrollbar */
    width: 0px;                /* Optional: remove scrollbar space */
}
</style>
